import { Download, Loader2, RefreshCw, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FacetCombobox } from "@/components/ui/facet-combobox";
import { useI18n } from "@/hooks/useI18n";
import { AgencyAdvisorMemberRow } from "@/integrations/supabase/rpc-types";

interface ClientsFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  advisorId: string | null;
  onAdvisorChange: (value: string | null) => void;
  /**
   * Assente per travel_designer e travel_advisor_member: filtrerebbero su un elenco che
   * contiene solo se stessi.
   */
  advisors?: AgencyAdvisorMemberRow[];
  isRefreshing: boolean;
  onRefresh: () => void;
  /** Scarica l'elenco corrente in XLSX. */
  onExport: () => void;
  isExporting: boolean;
  /**
   * Falso quando la lista e' vuota: un file con le sole intestazioni sembra un export
   * riuscito e non lo e'.
   */
  canExport: boolean;
}

export function ClientsFilters({
  search,
  onSearchChange,
  advisorId,
  onAdvisorChange,
  advisors,
  isRefreshing,
  onRefresh,
  onExport,
  isExporting,
  canExport,
}: ClientsFiltersProps) {
  const { t } = useI18n();

  // Basta che il chiamante li passi, come nei filtri dei pagamenti: sono admin e agency,
  // gli unici che vedono piu' di un advisor. Nasconderli sotto una soglia di opzioni
  // farebbe sparire un filtro atteso solo perche' oggi il team e' di una persona.
  const showAdvisorFilter = advisors !== undefined;

  const advisorOptions = (advisors ?? []).map((advisor) => ({
    value: advisor.advisor_id,
    label:
      [advisor.first_name, advisor.last_name].filter(Boolean).join(" ") ||
      advisor.email ||
      advisor.advisor_id,
    hint: advisor.email,
  }));

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t("dashboard.overview.clients.searchPlaceholder")}
          className="pl-9 pr-9"
          aria-label={t("dashboard.overview.clients.searchPlaceholder")}
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
            aria-label={t("dashboard.overview.clients.clearSearch")}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {showAdvisorFilter && (
        <FacetCombobox
          options={advisorOptions}
          value={advisorId}
          onChange={onAdvisorChange}
          placeholder={t("dashboard.overview.clients.advisorFilter")}
          searchPlaceholder={t("dashboard.overview.clients.advisorSearch")}
          clearLabel={t("dashboard.overview.clients.advisorClear")}
          emptyLabel={t("dashboard.overview.clients.advisorEmpty")}
          className="min-w-[180px]"
        />
      )}

      {/*
        L'export sta accanto ai filtri e non in cima alla pagina perche' esporta cio' che i
        filtri hanno selezionato, non l'anagrafica intera: separarli farebbe credere che il
        file contenga sempre tutti i clienti.
      */}
      <Button
        variant="outline"
        size="sm"
        onClick={onExport}
        disabled={isExporting || !canExport}
      >
        {isExporting ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Download className="h-4 w-4 mr-2" />
        )}
        {t("dashboard.overview.clients.exportList.button")}
      </Button>

      <Button
        variant="outline"
        size="icon"
        onClick={onRefresh}
        disabled={isRefreshing}
        aria-label={t("dashboard.retry")}
      >
        <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
      </Button>
    </div>
  );
}

export default ClientsFilters;
